/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  ReactNode,
  useCallback,
} from "react";
import {
  fetchUserStats,
  setActiveProfile,
  UserData,
  UserStats,
  UserProfile,
} from "../services/api";
import { useNavigate } from "react-router-dom";
import {
  clearStudioSession,
  getStudioSessionFromCookie,
} from "../lib/studioAuth";

export const PROFILE_SWITCHED_EVENT = "profileSwitched";
export const USER_LOGGED_IN_EVENT = "userLoggedIn";
const SUPPRESS_SUBSCRIPTION_RESUME_KEY = "suppressSubscriptionResume";

interface AuthContextType {
  user: UserData["user"] | null;
  profiles: UserProfile[];
  activeProfile: UserProfile | null;
  userStats: UserStats | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isSubscribed: boolean;
  setActiveProfileById: (profileId: string) => Promise<void>;
  refreshUserData: () => Promise<void>;
  logout: (options?: {
    redirectTo?: string;
    skipApi?: boolean;
  }) => Promise<void>;
  fetchStats: () => Promise<UserStats | null>;
  fetchStatsLazy: () => Promise<UserStats | null>;
  switchProfile: (profileId: string) => Promise<boolean>;
  checkSubscriptionStatus: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
};

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const getToken = (): string | null =>
  sessionStorage.getItem("token") || localStorage.getItem("token");

const getStudioAccessToken = (): string | null => {
  const studioSession = getStudioSessionFromCookie();

  if (!studioSession?.access_token) {
    return null;
  }

  return studioSession.access_token;
};

const isSubscribedFromProfile = (profileData: any): boolean => {
  const subscriptionInfo = profileData?.subscription_info;
  return (
    subscriptionInfo?.is_subscribed === true ||
    subscriptionInfo?.is_subscribed === 1 ||
    subscriptionInfo?.is_active === true ||
    subscriptionInfo?.is_active === 1
  );
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<UserData["user"] | null>(null);
  const [profiles, setProfiles] = useState<UserProfile[]>([]);
  const [activeProfile, setActiveProfileState] = useState<UserProfile | null>(
    null,
  );
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSwitchingProfile, setIsSwitchingProfile] = useState(false); // Add this state
  const navigate = useNavigate();
  const isRefreshing = useRef(false);
  const initialLoadDone = useRef(false);

  const clearFrontendAuthState = useCallback(() => {
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("refreshToken");
    sessionStorage.removeItem("user");
    sessionStorage.removeItem("redirectAfterLogin");
    sessionStorage.removeItem("redirectAfterSubscription");
    sessionStorage.removeItem("authRedirecting");
    sessionStorage.removeItem("ekluvya.walkthrough.pending");
    sessionStorage.removeItem(SUPPRESS_SUBSCRIPTION_RESUME_KEY);
    sessionStorage.removeItem("pendingLoginHint");
    sessionStorage.removeItem("pendingConflictToken");
    sessionStorage.removeItem("pendingDeviceId");
    sessionStorage.removeItem("pendingDeviceName");
    sessionStorage.removeItem("pendingSessionId");
    sessionStorage.removeItem("pendingForceLogout");
    sessionStorage.removeItem("activeProfileId");
    sessionStorage.removeItem("activeProfileData");
    sessionStorage.removeItem("studioBridgeUser");
    sessionStorage.removeItem("sessionId");
    localStorage.removeItem("token");
    localStorage.removeItem("authToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
    setIsAuthenticated(false);
    setIsSubscribed(false);
    setUser(null);
    setProfiles([]);
    setActiveProfileState(null);
    setUserStats(null);
  }, []);

  const clearAuthState = useCallback(() => {
    clearFrontendAuthState();
    clearStudioSession();
  }, [clearFrontendAuthState]);

  const redirectToLogin = useCallback(() => {
    clearAuthState();
    navigate("/login", { replace: true });
  }, [clearAuthState, navigate]);

  const fetchUserProfile = useCallback(async (token: string) => {
    try {
      const res = await fetch(`${API_BASE_URL}/auth/profile`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!res.ok) return null;
      const d = await res.json();
      return d.user || d.data?.user || d.data || null;
    } catch {
      return null;
    }
  }, []);

  const fetchProfilesList = useCallback(
    async (token: string): Promise<UserProfile[]> => {
      try {
        const res = await fetch(`${API_BASE_URL}/profile/list`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        if (!res.ok) return [];
        const d = await res.json();
        if (Array.isArray(d.response?.data)) return d.response.data;
        if (Array.isArray(d.data)) return d.data;
        if (Array.isArray(d.response)) return d.response;
        if (Array.isArray(d)) return d;
        return [];
      } catch {
        return [];
      }
    },
    [],
  );

  const createFrontendSessionFromStudio = useCallback(async () => {
    const studioToken = getStudioAccessToken();

    if (!studioToken) {
      return null;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/auth/frontend-session`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${studioToken}`,
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) {
        return null;
      }

      const data = await res.json();
      const token = data?.token || data?.accessToken;

      if (!token) {
        return null;
      }

      sessionStorage.setItem("token", token);
      sessionStorage.setItem(
        "studioBridgeUser",
        JSON.stringify(data?.user || data?.studioUser || {}),
      );

      if (data?.refreshToken) {
        sessionStorage.setItem("refreshToken", data.refreshToken);
      }

      return token;
    } catch (error) {
      console.error("Unable to create frontend session from Studio:", error);
      return null;
    }
  }, []);

  const resolveActiveProfile = useCallback(
    (list: UserProfile[]): UserProfile | null => {
      if (!list.length) return null;
      const storedId = sessionStorage.getItem("activeProfileId");
      if (storedId) {
        const found = list.find(
          (p: any) => p.id === storedId || p._id === storedId,
        );
        if (found) return found;
      }
      const def = list.find(
        (p: any) => p.is_default_profile === 1 || p.is_default_profile === true,
      );
      return def ?? list[0];
    },
    [],
  );

  const refreshUserData = useCallback(async () => {
    let token = getToken();
    const localToken = localStorage.getItem("token");

    if (!token) {
      clearFrontendAuthState();
      token = await createFrontendSessionFromStudio();
    }

    if (isRefreshing.current) return;
    isRefreshing.current = true;

    try {
      setIsLoading(true);
      // The profile list only needs the token, so it is requested alongside the profile.
      const listToken = token;
      const profilesListRequest = token ? fetchProfilesList(token) : null;
      let profileData = token ? await fetchUserProfile(token) : null;
      let profilesList: UserProfile[] = [];

      if (!profileData) {
        if (token) {
          redirectToLogin();
          return;
        }

        clearFrontendAuthState();
        const bridgedToken = await createFrontendSessionFromStudio();

        if (bridgedToken && bridgedToken !== token) {
          token = bridgedToken;
          profileData = await fetchUserProfile(token);
          if (!profileData) {
            redirectToLogin();
            return;
          }
        }
      }

      if (profileData) {
        profilesList =
          profilesListRequest && token === listToken
            ? await profilesListRequest
            : token
              ? await fetchProfilesList(token)
              : [];
        const studioBridgeUser = JSON.parse(
          sessionStorage.getItem("studioBridgeUser") || "{}",
        );

        if (Object.keys(studioBridgeUser).length) {
          profileData = {
            ...profileData,
            ...studioBridgeUser,
          };
        }

        setUser(profileData);
        setIsSubscribed(isSubscribedFromProfile(profileData));
        sessionStorage.setItem("user", JSON.stringify(profileData));
        if (token) {
          sessionStorage.setItem("token", token);
        }
        const refreshToken =
          sessionStorage.getItem("refreshToken") ||
          localStorage.getItem("refreshToken");
        if (refreshToken) {
          sessionStorage.setItem("refreshToken", refreshToken);
        }
        if (localToken) {
          localStorage.removeItem("token");
          localStorage.removeItem("authToken");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("user");
        }
        setIsAuthenticated(true);
      } else {
        clearFrontendAuthState();
        setIsAuthenticated(false);
      }

      setProfiles(profilesList);
      const active = resolveActiveProfile(profilesList);
      setActiveProfileState(active);
      setUserStats(null);
    } catch (err) {
      console.error("refreshUserData error:", err);
      clearFrontendAuthState();
    } finally {
      setIsLoading(false);
      isRefreshing.current = false;
    }
  }, [
    clearFrontendAuthState,
    createFrontendSessionFromStudio,
    fetchUserProfile,
    fetchProfilesList,
    resolveActiveProfile,
    redirectToLogin,
  ]);

  const switchProfile = useCallback(
    async (profileId: string) => {
      if (!isAuthenticated) {
        console.warn("Cannot switch profile: user not authenticated");
        return false;
      }

      try {
        const token = getToken();
        if (!token) {
          console.warn("No token found");
          return false;
        }

        setIsSwitchingProfile(true);

        console.log("Switching to profile:", profileId);
        console.log("API URL:", `${API_BASE_URL}/profile/active/${profileId}`);

        const response = await fetch(
          `${API_BASE_URL}/profile/active/${profileId}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            credentials: "include",
          },
        );

        console.log("Response status:", response.status);

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error("Switch profile API error:", errorData);
          throw new Error(
            errorData.message || `Failed to switch profile: ${response.status}`,
          );
        }

        const result = await response.json();
        console.log("Switch profile result:", result);

        if (result.statusCode === 200) {
          // Find the full profile from the list
          const profileFromList = profiles.find(
            (p: any) => p._id === profileId || p.id === profileId,
          );

          const newActiveProfile = result.response || profileFromList;

          if (newActiveProfile) {
            // Update the active profile in state
            setActiveProfileState({
              ...newActiveProfile,
              id: newActiveProfile._id || newActiveProfile.id,
              _id: newActiveProfile._id || newActiveProfile.id,
            });
          }

          // Store the active profile ID in session storage
          sessionStorage.setItem("activeProfileId", profileId);

          // Dispatch event to notify other components
          window.dispatchEvent(
            new CustomEvent(PROFILE_SWITCHED_EVENT, {
              detail: { profile: newActiveProfile, profileId },
            }),
          );

          return true;
        }

        return false;
      } catch (error) {
        console.error("Error switching profile:", error);
        return false;
      } finally {
        setIsSwitchingProfile(false);
      }
    },
    [isAuthenticated, profiles],
  );

  const setActiveProfileById = async (profileId: string) => {
    try {
      await setActiveProfile(profileId);
      const profile = profiles.find(
        (p: any) => p.id === profileId || p._id === profileId,
      );
      if (profile) {
        setActiveProfileState(profile);
        if (userStats) setUserStats({ ...userStats, name: profile.first_name });
      }
      window.dispatchEvent(
        new CustomEvent(PROFILE_SWITCHED_EVENT, { detail: { profileId } }),
      );
    } catch (err) {
      console.error("setActiveProfileById error:", err);
      throw err;
    }
  };

  const fetchStatsLazy = useCallback(async (): Promise<UserStats | null> => {
    if (userStats) return userStats;
    const token = getToken();
    if (!token) return null;
    try {
      const stats = await fetchUserStats();
      setUserStats(stats);
      return stats;
    } catch {
      const fallback: UserStats = {
        name:
          (activeProfile as any)?.first_name ??
          (user as any)?.firstName ??
          "User",
        className: "Class 10",
        streak: 0,
        xp: 0,
        badgesEarned: 0,
      };
      setUserStats(fallback);
      return fallback;
    }
  }, [userStats, activeProfile, user]);

  const logout = async (options?: {
    redirectTo?: string;
    skipApi?: boolean;
  }) => {
    sessionStorage.setItem("logoutInProgress", "1");

    const token = getToken();
    const sessionId =
      sessionStorage.getItem("sessionId") ||
      sessionStorage.getItem("pendingSessionId") ||
      null;
    const deviceId =
      sessionStorage.getItem("deviceId") ||
      sessionStorage.getItem("pendingDeviceId") ||
      null;

    if (token && !options?.skipApi) {
      try {
        await fetch(`${API_BASE_URL}/auth/logout`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ deviceId, sessionId }),
          keepalive: true,
        });
      } catch (error) {
        console.error("Logout request failed:", error);
      }
    }

    clearAuthState();
    setTimeout(() => {
      sessionStorage.removeItem("logoutInProgress");
    }, 500);

    navigate(options?.redirectTo ?? "/", { replace: true });
  };

  const checkSubscriptionStatus = useCallback(async (): Promise<boolean> => {
    const token = getToken();
    if (!token) return false;

    try {
      const response = await fetch(`${API_BASE_URL}/subscriptions/status`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();
      const subscribed =
        data?.response?.is_subscribed === true ||
        data?.response?.is_subscribed === 1 ||
        data?.is_subscribed === true ||
        data?.is_subscribed === 1;
      setIsSubscribed(subscribed);
      return subscribed;
    } catch (error) {
      console.error("Error checking subscription status:", error);
      return false;
    }
  }, []);

  // Initial load effect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const shouldLogout =
      params.get("logout") === "1" || params.get("logout") === "true";

    if (shouldLogout) {
      clearAuthState();
      setIsLoading(false);
      initialLoadDone.current = true;
      window.location.replace("/");
      return;
    }

    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      refreshUserData();
    }
  }, [clearAuthState, refreshUserData]);

  // Storage event listener for token changes
  useEffect(() => {
    let debounce: ReturnType<typeof setTimeout>;
    const handleStorage = (e: StorageEvent) => {
      if (e.key !== "token") return;
      clearTimeout(debounce);
      debounce = setTimeout(() => {
        if (e.newValue) refreshUserData();
        else if (isAuthenticated) logout();
      }, 150);
    };
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("storage", handleStorage);
      clearTimeout(debounce);
    };
  }, [isAuthenticated, refreshUserData]);

  // Check subscription status on authentication
  useEffect(() => {
    if (isAuthenticated) {
      checkSubscriptionStatus();
    }
  }, [isAuthenticated, checkSubscriptionStatus]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const refreshSubscription = () => {
      void checkSubscriptionStatus();
    };
    window.addEventListener("subscriptionCompleted", refreshSubscription);
    window.addEventListener("subscriptionActivated", refreshSubscription);
    return () => {
      window.removeEventListener("subscriptionCompleted", refreshSubscription);
      window.removeEventListener("subscriptionActivated", refreshSubscription);
    };
  }, [isAuthenticated, checkSubscriptionStatus]);

  // Redirect after subscription
  useEffect(() => {
    const redirectAfterSubscription = sessionStorage.getItem(
      "redirectAfterSubscription",
    );
    const suppressSubscriptionResume =
      sessionStorage.getItem(SUPPRESS_SUBSCRIPTION_RESUME_KEY) === "1";
    if (redirectAfterSubscription && isAuthenticated && isSubscribed) {
      if (suppressSubscriptionResume) {
        return;
      }
      sessionStorage.removeItem("redirectAfterSubscription");
      sessionStorage.removeItem("lockedVideoId");
      sessionStorage.setItem("ekluvya.walkthrough.pending", "1");
      window.dispatchEvent(new CustomEvent("subscriptionActivated"));
      navigate(redirectAfterSubscription);
    }
  }, [isAuthenticated, isSubscribed, navigate]);

  const value: AuthContextType = {
    user,
    profiles,
    activeProfile,
    userStats,
    isLoading,
    isAuthenticated,
    isSubscribed,
    setActiveProfileById,
    refreshUserData,
    logout,
    fetchStats: fetchStatsLazy,
    fetchStatsLazy,
    switchProfile,
    checkSubscriptionStatus,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
